import { initTable } from '../../../../../ui/components/pure/list-table/list-table.init';
import {
  KnownDeliveryDtoDeliveryType,
  KnownDeliveryDtoState,
  KnownSortOrder
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { TableComponents } from '../../../../../ui/components/pure/list-table/list-table-cells.component';
import {
  COLUMN_WIDTHS,
  pinned,
  ULTRA_WIDE_SCALE,
  useIsUltraWide,
  WRAP_ANYWHERE
} from '../../../../../ui/components/pure/list-table/list-table-column-styles';
import { useCallback, useMemo } from 'react';
import { MenuItem, TextField } from '@mui/material';
import { DeliveryStatusOptions } from '../../../../deliveries/list/logic/deliveries-list-props.map';
import {
  InfiniteScrollProps,
  InfiniteTable,
  PaginatedRow
} from '../../../../../ui/components/pure/list-table-wrappers/infinite-table.component';
import { MultipleNotificationColumn } from '../../../../notifications/components/multiple-notification-column';
import { Delivery } from '../../../../deliveries/models/delivery.model';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { getDeliveryStateLabelForProject } from '../../../../../core/util/status-label.util';
import { useBookingFlowType } from '../../../../orders/list/actions/book-order/use-booking-flow-type.hook';
import { UseSearchParamsOptions } from '../../../../../hooks/use-search-params.hook';

export interface UserDeliveriesListControlsState {
  sortBy: string;
  sortOrder: KnownSortOrder;
  state: KnownDeliveryDtoState | '';
}

export const deliveriesListInitialState: UserDeliveriesListControlsState = {
  sortBy: 'deliveredTime',
  sortOrder: KnownSortOrder.Desc,
  state: ''
};

export const deliveriesListSearchParamOptions: UseSearchParamsOptions<UserDeliveriesListControlsState> = {
  parse: {
    excludedValues: [''],
    convertBooleans: true
  },
  stringify: {
    excludedValues: [null, ''],
    stringifyDates: true
  }
};

function useUserDeliveriesTable(projectID: string, isCourier: boolean) {
  const bookingFlowType = useBookingFlowType(projectID);
  const isUltraWide = useIsUltraWide();
  return useMemo(() => {
    const tb = initTable<Delivery>();
    tb.styles({
      table: { tableLayout: 'fixed' }
    });

    // Desktop columns. This table sits in a narrow card and FROM is mostly a logo, so FROM is pinned at
    // the logo width and the two times share the slack, keeping each on one line.
    tb.column('TYPE', TableComponents.SVGCell, {
      SVG: ':deliveryTypeIcon',
      iconSize: `~24px`
    }).options({
      minBreakpoint: 'md',
      headerStyles: pinned(COLUMN_WIDTHS.icon)
    });
    tb.column('FROM', TableComponents.TenantLogo, {
      name: (row) => {
        if (row.deliveryType === KnownDeliveryDtoDeliveryType.RetailOrder) {
          return row.project.name;
        } else {
          return row.deliveredBy?.party?.name || '';
        }
      },
      imageURL: (row) => {
        if (row.deliveryType === KnownDeliveryDtoDeliveryType.RetailOrder) {
          return row.project.imageURL;
        } else {
          return row.deliveredBy?.party?.imageURL || '';
        }
      }
    }).options({
      isSortable: true,
      sortId: 'deliveredBy.party.name',
      minBreakpoint: 'md',
      tooltip: 'retailer name',
      styles: WRAP_ANYWHERE,
      headerStyles: pinned(
        isUltraWide ? `${parseFloat(COLUMN_WIDTHS.by) * ULTRA_WIDE_SCALE.logoOrLabel}px` : COLUMN_WIDTHS.by
      )
    });
    tb.column('DELIVERY STARTED TIME', TableComponents.DateTime, {
      value: ':createdTime'
    }).options({
      isSortable: true,
      sortId: 'createdTime',
      minBreakpoint: 'md',
      tooltip: 'delivery started time'
    });
    tb.column('COLLECTED/CANCELLED', TableComponents.DateTime, {
      value: (row) => row.collectedTime || row.cancelledTime
    }).options({
      isSortable: true,
      sortId: 'collectedTime',
      minBreakpoint: 'md',
      tooltip: 'collected time'
    });
    tb.column('STATUS', TableComponents.WorkflowStatus, {
      label: (row) => {
        return getDeliveryStateLabelForProject(row.state, bookingFlowType);
      },
      color: ':stateColor'
    }).options({
      isSortable: true,
      sortId: 'state',
      minBreakpoint: 'md',
      tooltip: 'delivery status',
      headerStyles: pinned(COLUMN_WIDTHS.status)
    });
    // Notifications are addressed to the recipient, not the courier, so the column says nothing
    // about a courier's own deliveries.
    if (!isCourier) {
      tb.column(
        {
          name: 'NOTIFICATIONS',
          render: () => (
            <div>
              <NotificationsNoneIcon />
            </div>
          )
        },
        MultipleNotificationColumn,
        {
          notifications(row) {
            return row.notifications;
          },
          align: '~left',
          iconSize: `~24px`,
          maxVisibleNotifications: '~1'
        }
      ).options({
        headerStyles: pinned(COLUMN_WIDTHS.notifications)
      });
    }
    // Mobile Columns
    tb.columnGroup('DELIVERY STARTED TIME')
      .row(TableComponents.DateTime, { value: ':createdTime' })
      .row(TableComponents.BoldText, { value: ':deliveredTo.party.name' })
      .options({
        isSortable: true,
        sortId: 'createdTime',
        maxBreakpoint: 'sm',
        tooltip: 'delivery started time'
      });
    tb.columnGroup('')
      .row(TableComponents.WorkflowStatus, {
        label: (row) => {
          return getDeliveryStateLabelForProject(row.state, bookingFlowType);
        },
        color: ':stateColor',
        align: '~centre'
      })
      .row(TableComponents.TenantLogo, {
        name: ':deliveredBy.party.name',
        imageURL: ':deliveredBy.party.imageURL'
      })
      .options({
        isSortable: false,
        maxBreakpoint: 'sm',
        styles: { display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }
      });

    tb.title('Deliveries');
    tb.rowStyles({ td: { height: '64px' } });

    return tb;
  }, [bookingFlowType, isCourier, isUltraWide]);
}

type UserOrdersListProps = {
  params: { projectID: string; shortcode?: string };
  formState: UserDeliveriesListControlsState;
  onFormInput: (update: Partial<UserDeliveriesListControlsState>) => void;
  pages: PaginatedRow<Delivery>[];
  infiniteScroll: InfiniteScrollProps;
  isLoading: boolean;
  isPreviousData: boolean;
  hasError: boolean;
  isCourier: boolean;
  // Shown against the selected status only, as it is the list query's own total.
  totalRecords?: number;
};

export function UserDeliveriesList({
  params,
  pages,
  infiniteScroll,
  formState,
  onFormInput,
  isLoading,
  isPreviousData,
  hasError,
  isCourier,
  totalRecords
}: UserOrdersListProps) {
  const tb = useUserDeliveriesTable(params.projectID, isCourier);

  const sortData = useMemo(
    () => ({ sortBy: formState.sortBy, sortOrder: formState.sortOrder }),
    [formState.sortBy, formState.sortOrder]
  );
  const mapRowURL = useCallback(
    (row: Delivery) => row.createDetailsPageUrl(params.shortcode || params.projectID),
    [params.projectID, params.shortcode]
  );

  tb.controls(UserDeliveriesListControls, { formState, onFormInput, totalRecords });
  tb.config({ mapRowURL });

  return (
    <InfiniteTable
      uniqueKey="id"
      pages={pages}
      infiniteScroll={infiniteScroll}
      table={tb}
      onSort={onFormInput}
      sortData={sortData}
      hasError={hasError}
      isLoading={isLoading}
      isPreviousData={isPreviousData}
    />
  );
}

function UserDeliveriesListControls({
  formState,
  onFormInput,
  totalRecords
}: Pick<UserOrdersListProps, 'formState' | 'onFormInput' | 'totalRecords'>) {
  return (
    <TextField
      select
      variant="outlined"
      size="small"
      value={formState.state}
      onChange={(e) =>
        onFormInput({
          state: e.target.value as UserDeliveriesListControlsState['state']
        })
      }
      slotProps={{
        select: { displayEmpty: true }
      }}
    >
      {DeliveryStatusOptions.map(({ label, value }) => {
        const count = value === formState.state ? totalRecords : undefined;
        return (
          <MenuItem key={value} value={value}>
            {count === undefined ? label : `${label} (${count.toLocaleString('en-AU')})`}
          </MenuItem>
        );
      })}
    </TextField>
  );
}
